import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PreviewIcon from '@mui/icons-material/Preview';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Grid,
  IconButton,
  Modal,
  Stack,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import Moment from 'react-moment';

const checkerBg = {
  backgroundColor: '#f7f7f7',
  backgroundImage:
    'linear-gradient(45deg, rgba(0,0,0,0.06) 25%, transparent 25%), linear-gradient(-45deg, rgba(0,0,0,0.06) 25%, transparent 25%), linear-gradient(45deg, transparent 75%, rgba(0,0,0,0.06) 75%), linear-gradient(-45deg, transparent 75%, rgba(0,0,0,0.06) 75%)',
  backgroundSize: '16px 16px',
  backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0',
};

const variantLabels = ['High Resolution', 'Large', 'Small', 'Thumbnail'];

const ResolutionContainer = ({ data, selectedKeys = [], onToggleSelect = () => {}, canDelete }) => {
  const [modalState, setModalState] = useState({ open: false, list: [], index: 0 });
  const [isFullView, setIsFullView] = useState(false);

  const normalizedData = useMemo(() => {
    return variantLabels.map((_, index) => {
      if (Array.isArray(data)) {
        return data[index] || [];
      }
      if (data && typeof data === 'object') {
        const variants = [data.hires || [], data.large || [], data.small || [], data.thumbnail || []];
        return variants[index] || [];
      }
      return [];
    });
  }, [data]);

  const sizeCalculator = (pic) => {
    if (!pic) return 'N/A';
    if (pic / 1024 < 1024) return `${Math.round(pic / 1024)} KB`;
    return `${Math.round(pic / 1024 / 1024)} MB`;
  };

  const extractSuffixToken = (key) => {
    if (!key) return null;
    const fileName = key.split('/').pop() || key;
    const match = fileName.match(/[_-]([A-Z0-9])(?=\.[^.]+$)/i);
    return match ? match[1].toUpperCase() : null;
  };

  const sortImages = (images = []) =>
    [...images].sort((a, b) => {
      const aS = extractSuffixToken(a.Key);
      const bS = extractSuffixToken(b.Key);
      if (aS === bS) return 0;
      if (!aS) return -1;
      if (!bS) return 1;
      return aS.localeCompare(bS);
    });

  const modalEntry =
    modalState.list && modalState.list.length ? modalState.list[modalState.index] : null;

  const openModal = (list, index) => {
    setIsFullView(false);
    setModalState({ open: true, list, index });
  };

  const closeModal = () => {
    setIsFullView(false);
    setModalState((prev) => ({ ...prev, open: false }));
  };

  const gatherVariantSet = (targetImage) => {
    if (!targetImage?.Key) return [];
    const targetFile = targetImage.Key.split('/').pop();
    const targetStem = targetFile?.replace(/\.[^.]+$/, '') || targetFile;
    const set = [];

    normalizedData.forEach((variantList) => {
      const matchExact = (variantList || []).find(
        (img) => (img.Key || '').split('/').pop() === targetFile
      );
      if (matchExact) {
        set.push(matchExact);
        return;
      }
      const matchStem = (variantList || []).find(
        (img) => ((img.Key || '').split('/').pop() || '').replace(/\.[^.]+$/, '') === targetStem
      );
      if (matchStem) {
        set.push(matchStem);
      }
    });

    return set.length ? set : [targetImage];
  };

  return (
    <Box>
      <Modal open={modalState.open} onClose={closeModal}>
        <>
          {isFullView && modalEntry && (
            <Box
              onClick={() => setIsFullView(false)}
              sx={{
                position: 'fixed',
                inset: 0,
                bgcolor: 'rgba(0,0,0,0.9)',
                zIndex: 1301,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'auto',
              }}
            >
              <Box
                component='img'
                src={`https://media.server.theshootingwarehouse.com/${modalEntry.Key}`}
                alt={modalEntry.Key}
                sx={{
                  width: 'auto',
                  height: 'auto',
                  maxWidth: 'none',
                  maxHeight: 'none',
                  cursor: 'zoom-out',
                }}
              />
            </Box>
          )}
          <Box
            sx={(theme) => ({
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              bgcolor: theme.palette.background.paper,
              borderRadius: 2,
              boxShadow: 24,
              p: 2,
              maxWidth: '90vw',
              maxHeight: '90vh',
              width: 'min(900px, 95vw)',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            })}
          >
            <Stack direction='row' justifyContent='space-between' alignItems='center'>
              <Typography variant='subtitle1' fontWeight={700}>
                Preview
              </Typography>
              <IconButton size='small' onClick={closeModal}>
                <CloseIcon />
              </IconButton>
            </Stack>
            {modalEntry ? (
              <>
                <Box
                  sx={{
                    width: '100%',
                    maxHeight: '70vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    ...checkerBg,
                    borderRadius: 1.5,
                    p: 1,
                  }}
                >
                  <Box
                    component='img'
                    src={`https://media.server.theshootingwarehouse.com/${modalEntry.Key}`}
                    alt={modalEntry.Key}
                    onClick={() => setIsFullView(true)}
                    sx={{
                      maxWidth: '95%',
                      maxHeight: '70vh',
                      objectFit: 'contain',
                      borderRadius: 1,
                      boxShadow: 2,
                      cursor: 'zoom-in',
                    }}
                  />
                </Box>
                <Stack spacing={0.5}>
                  <Typography variant='body2' color='text.secondary'>
                    Key: {modalEntry.Key}
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    File size: {sizeCalculator(modalEntry.Size)}
                  </Typography>
                  {modalEntry.LastModified && (
                    <Typography variant='body2' color='text.secondary'>
                      Modified: <Moment format='MM/DD/YYYY'>{modalEntry.LastModified}</Moment>
                    </Typography>
                  )}
                </Stack>
                <Stack direction='row' spacing={1} justifyContent='space-between' alignItems='center'>
                  <CopyToClipboard
                    text={`https://media.server.theshootingwarehouse.com/${modalEntry.Key}`}
                  >
                    <Button startIcon={<ContentCopyIcon />} variant='outlined'>
                      Copy Link
                    </Button>
                  </CopyToClipboard>
                  <Stack direction='row' spacing={0.5}>
                    <IconButton
                      disabled={modalState.index === 0}
                      onClick={() =>
                        setModalState((prev) => ({ ...prev, index: Math.max(prev.index - 1, 0) }))
                      }
                    >
                      <ArrowBackIosNewIcon fontSize='small' />
                    </IconButton>
                    <IconButton
                      disabled={modalState.index >= modalState.list.length - 1}
                      onClick={() =>
                        setModalState((prev) => ({
                          ...prev,
                          index: Math.min(prev.index + 1, prev.list.length - 1),
                        }))
                      }
                    >
                      <ArrowForwardIosIcon fontSize='small' />
                    </IconButton>
                  </Stack>
                </Stack>
              </>
            ) : null}
          </Box>
        </>
      </Modal>

      <Grid container spacing={2}>
        {normalizedData.map((variant, index) => {
          const sortedVariant = sortImages(variant);

          return (
            <Grid item xs={12} md={6} lg={3} key={variantLabels[index]}>
              <Box
                sx={{
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  overflow: 'hidden',
                  height: '100%',
                }}
              >
                <Box
                  sx={{
                    px: 2,
                    py: 1.25,
                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                    bgcolor: (theme) =>
                      theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
                  }}
                >
                  <Typography variant='subtitle2' fontWeight={700}>
                    {variantLabels[index]}
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    {sortedVariant.length} image{sortedVariant.length === 1 ? '' : 's'}
                  </Typography>
                </Box>

                <Stack spacing={1.5} sx={{ p: 1.5 }}>
                  {sortedVariant.length ? (
                    sortedVariant.map((image) => {
                      const isSelected = selectedKeys.includes(image.Key);

                      return (
                        <Box
                          key={image.Key}
                          sx={{
                            border: (theme) => `1px solid ${theme.palette.divider}`,
                            borderRadius: 1.5,
                            p: 1,
                          }}
                        >
                          <Box
                            sx={{
                              height: 180,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mb: 1,
                              borderRadius: 1,
                              overflow: 'hidden',
                              ...checkerBg,
                            }}
                          >
                            <Box
                              component='img'
                              src={`https://media.server.theshootingwarehouse.com/${image.Key}`}
                              alt={image.Key}
                              sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                            />
                          </Box>

                          <Stack spacing={0.5}>
                            <Typography
                              variant='caption'
                              color='text.secondary'
                              sx={{
                                display: 'block',
                                wordBreak: 'break-all',
                                lineHeight: 1.35,
                              }}
                            >
                              {image.Key}
                            </Typography>
                            <Typography variant='caption' color='text.secondary'>
                              Size: {sizeCalculator(image.Size)}
                            </Typography>
                            <Typography variant='caption' color='text.secondary'>
                              Modified:{' '}
                              {image.LastModified ? <Moment format='L'>{image.LastModified}</Moment> : 'N/A'}
                            </Typography>
                          </Stack>

                          <Divider sx={{ my: 1 }} />

                          <Stack direction='row' spacing={0.5} justifyContent='space-between'>
                            <Stack direction='row' spacing={0.5}>
                              <IconButton
                                size='small'
                                onClick={() => openModal(gatherVariantSet(image), 0)}
                                title='Preview image set'
                              >
                                <PreviewIcon fontSize='small' />
                              </IconButton>
                              <CopyToClipboard
                                text={`https://media.server.theshootingwarehouse.com/${image.Key}`}
                              >
                                <IconButton size='small' title='Copy image URL'>
                                  <ContentCopyIcon fontSize='small' />
                                </IconButton>
                              </CopyToClipboard>
                              <IconButton
                                size='small'
                                component='a'
                                href={`https://media.server.theshootingwarehouse.com/${image.Key}`}
                                target='_blank'
                                rel='noopener noreferrer'
                                title='Open image in new tab'
                              >
                                <OpenInNewIcon fontSize='small' />
                              </IconButton>
                            </Stack>
                            {canDelete && (
                              <Checkbox
                                checked={isSelected}
                                onChange={(event) => onToggleSelect(image.Key, event.target.checked)}
                                inputProps={{ 'aria-label': `select ${image.Key}` }}
                              />
                            )}
                          </Stack>
                        </Box>
                      );
                    })
                  ) : (
                    <Typography variant='body2' color='text.secondary'>
                      No images available.
                    </Typography>
                  )}
                </Stack>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default ResolutionContainer;
